import { getHomeSize, getLivingSpace, getMortgagesAmount, getPropertyType } from '@habity/services';
import { formatCurrency, capitalizeFirstLetter } from '@habity/utils';
import { DataFieldConfig, forEstateTypes } from './data-builder';
import { getPlotSize } from '@habity/services/addresses/plotSizes';

const getTenureValue = (value?: string) => {
  switch (value) {
    case 'Frikopt':
      return 'Friköpt';
    case 'Tomtratt':
      return 'Tomträtt';
    default:
      return value || '';
  }
}

export const propertyFields: DataFieldConfig[] = [
  // Basic property information
  {
    key: 'baseInformation.propertyType',
    label: 'Bostadstyp',
    formatter: (value, context) => 
      getPropertyType(
        context?.filteredProviderData && 
        context?.provider_type && 
        context?.isApartment, 
        value,
        context?.provider_type
      ),
  },

  {
    key: 'plotBaseInformation.propertyType',
    label: 'Typ',
  },

    {
    key: 'propertyTypeAndAreas.propertyType',
    label: 'Typ',
  },
  
  {
    key: 'baseInformation.objectAddress.municipality',
    label: 'Kommun',
  },

  {
    key: 'plotBaseInformation.objectAddress.municipality',
    label: 'Kommun',
  },
  
  {
    key: 'baseInformation.objectAddress.area',
    label: 'Område',
  },

  {
    key: 'plotBaseInformation.objectAddress.area',
    label: 'Område',
  },

  // Cottage specific - winter insulated
  {
    key: 'advertiseOn.allYear',
    label: 'Vinterbonat',
    estateTypes: forEstateTypes('Cottage'),
    condition: (context) => {
      const value = context.filteredProviderData?.advertiseOn?.allYear;
      return value !== undefined && value !== null;
    },
    formatter: (value) => value ? 'Ja' : 'Nej',
  },
    
  // Property designation from address
  {
    key: 'property_designation',
    label: 'Fastighetsbeteckning',
    source: 'address',
  },



  {
    key: 'plotInfo.area',
    label: 'Tomtstorlek',
    formatter: (value) => getPlotSize(value, false),
  },

  {
    key: 'plotInfo.plotType',
    label: 'Tomttyp',
  },

  // Home size (requires both living_space and rooms from address)
  {
    key: 'living_space',
    label: 'Storlek',
    source: 'address',
    estateTypes: forEstateTypes('House', 'Cottage', 'HousingCooperative', 'Condominium', 'CommercialProperty', 'Plot'),
    condition: (context) => {
      const value = context.address?.living_space;
      return value && context.address?.rooms;
    },
    formatter: (value, context) => getHomeSize(context.home),
  },


    {
      key: 'mainResidentialBuilding.numberOfRooms',
      label: 'Storlek',
      estateTypes: forEstateTypes('Farm'),
      formatter: (value, context) => {
        const livingSpace = context?.filteredProviderData?.mainResidentialBuilding?.livingSpace;
        const otherSpace = context?.filteredProviderData?.mainResidentialBuilding?.otherSpace;
        return `${value} rum och kök, ${getLivingSpace(livingSpace)} boarea${otherSpace > 0 ? `, ${getLivingSpace(otherSpace)} biarea` : ''}`
      }
    },
  

  // Bedrooms
  {
    key: 'bedrooms',
    label: 'Varav sovrum',
    source: 'address',
    estateTypes: forEstateTypes('House', 'Cottage', 'HousingCooperative', 'Condominium', 'CommercialProperty', 'Plot'),
    condition: (context) => {
      const value = context.address?.bedrooms;
      return value && context.address?.living_space && context.address?.rooms;
    },
    formatter: (value, context) => 
      `${value}${context.address?.bedrooms_max ? `-${context.address.bedrooms_max}` : ''} st`,
  },

  {
    key: 'mainResidentialBuilding.numberOfBedRooms.minValue',
    label: 'Varav sovrum',
    estateTypes: forEstateTypes('Farm'),
    formatter: (value, context) => 
      `${value}${context.filteredProviderData?.mainResidentialBuilding?.numberOfBedRooms?.maxValue ? `-${context.filteredProviderData?.mainResidentialBuilding?.numberOfBedRooms?.maxValue}` : ''} st`,
  },

  {
    key: 'propertyTypeAndAreas.numberOfpartition',
    label: 'Skiften',
  },

  {
    key: 'propertyTypeAndAreas.area',
    label: 'Markareal',
    condition: (context) => {
      const value = context.filteredProviderData?.propertyTypeAndAreas?.area;
      return value && value > 0;
    },
    formatter: (value) => `${Number(value?.toFixed(3))} hektar`,
  },

  // Area source
  {
    key: 'baseInformation.areaSource',
    label: 'Areakälla',
    formatter: (value, context) => {
      const comment = context.filteredProviderData?.baseInformation?.areaSourceComment;
      return `${value}${comment ? ` (${comment})` : ''}`;
    },
  },

  {
    key: 'plotBaseInformation.areaSource',
    label: 'Areakälla',
    formatter: (value, context) => {
      const comment = context.filteredProviderData?.plotBaseInformation?.areaSourceComment;
      return `${value}${comment ? ` (${comment})` : ''}`;
    },
  },







  // Housing cooperative specific fields
  {
    key: 'floorAndElevator.floor',
    label: 'Våning',
    estateTypes: forEstateTypes('HousingCooperative'),
    condition: (context) => {
      const floorData = context.filteredProviderData?.floorAndElevator;
      return floorData?.floor !== undefined || 
             floorData?.totalNumberOfFloors !== undefined || 
             floorData?.floorCommentary !== undefined;
    },
    formatter: (value, context) => {
      const { floor, totalNumberOfFloors, floorCommentary } = 
        context.filteredProviderData?.floorAndElevator || {};
      return `${floor !== undefined && totalNumberOfFloors ? `${floor} av ${totalNumberOfFloors}` : floor !== undefined ? floor : ''}${floorCommentary ? ` (${floorCommentary})` : ''}`;
    },
  },

  {
    key: 'floorAndElevator.elevator',
    label: 'Hiss',
    estateTypes: forEstateTypes('HousingCooperative'),
    condition: (context) => {
      const value = context.filteredProviderData?.floorAndElevator?.elevator;
      return value && context.isApartment;
    },
    formatter: (value, context) => {
      const description = context.filteredProviderData?.floorAndElevator?.descriptionOfElevator;
      return `${value}${description ? ` (${description})` : ''}`;
    },
  },

  {
    key: 'baseInformation.apartmentNumber',
    label: 'Lägenhetsnummer',
    estateTypes: forEstateTypes('HousingCooperative'),
    formatter: (value, context) => {
      const registration = context.filteredProviderData?.baseInformation?.apartmentNumberRegistration;
      return `${value}${registration ? ` (${registration})` : ''}`;
    },
  },

  // Price information
  {
    key: 'price.startingPrice',
    label: (context) => {
      const customPriceText = context?.filteredProviderData?.price?.text;
      const status = context?.home?.status;
      
      // Use same logic as price accordion
      return status?.name === 'sold'
        ? 'Slutpris'
        : customPriceText ? capitalizeFirstLetter(customPriceText) : 'Föreslagen budstart';
    },
    formatter: (value) => {
      return formatCurrency(value);
    },
    condition: (context) => {
      const value = context.filteredProviderData?.price?.startingPrice;
      return !!value;
    },
  },

  {
    key: 'date.possibleAccessDate',
    label: 'Tillträde',
    condition: () => true, // Always show this field
    formatter: (value) => {
      if (!value) return 'Enligt överenskommelse';
      try {
        return value;
        // const date = new Date(value);
        // date.toLocaleDateString('sv-SE', { 
        //   day: 'numeric', 
        //   month: 'long', 
        //   year: 'numeric' 
        // });
      } catch {
        return 'Enligt överenskommelse';
      }
    },
  },

  // Monthly fee for housing cooperatives
  {
    key: 'baseInformation.monthlyFee',
    label: 'Månadsavgift',
    estateTypes: forEstateTypes('HousingCooperative'),
    formatter: (value) => formatCurrency(value),
  },

  // Base information commentary
  {
    key: 'baseInformation.commentary',
    label: ' ',
    estateTypes: forEstateTypes('HousingCooperative'),
    condition: (context) => {
      return !!context.filteredProviderData?.baseInformation?.commentary;
    },
  },

  // Participation and repair fund - share of association
  {
    key: 'participationAndRepairFund.shareOfAssociation',
    label: 'Andelstal',
    estateTypes: forEstateTypes('HousingCooperative'),
    condition: (context) => {
      const { participationInAssociation, participationOffAnnualFee } = 
        context.filteredProviderData?.participationAndRepairFund || {};
      return participationInAssociation || participationOffAnnualFee;
    },
    formatter: (value, context) => {
      const { participationInAssociation, participationOffAnnualFee } = 
        context.filteredProviderData?.participationAndRepairFund || {};
      const shareOfAssociation: string[] = [];
      if (participationInAssociation) shareOfAssociation.push(`Andel i förening ${participationInAssociation?.toFixed(3)}%`);
      if (participationOffAnnualFee) shareOfAssociation.push(`Andel av årsavgift ${participationOffAnnualFee?.toFixed(3)}%`);
      return shareOfAssociation.join(', ');
    },
  },

  // Indirect net debt with comment
  {
    key: 'participationAndRepairFund.indirectNetDebt',
    label: 'Indirekt nettoskuldsättning',
    estateTypes: forEstateTypes('HousingCooperative'),
    condition: () => true, // Always show for housing cooperatives
    formatter: (value, context) => {
      const indirectNetDebt = context.filteredProviderData?.participationAndRepairFund?.indirectNetDebt;
      const providerComment = context.filteredProviderData?.participationAndRepairFund?.indirectNetDebtComment;
      const defaultComment = 'Bostadsrättens indirekta nettoskuldsättning anger hur stor andel av bostadsrättsföreningens lån som indirekt belastar bostadsrätten. Andelen beräknas som andelstalet för avgifter multiplicerat med föreningens nettoskuldsättning. Den består av räntebärande skulder med avdrag för räntebärande tillgångar och likvida medel. Underlagen för beräkningen kommer från föreningen.';
      
      const comment = providerComment || defaultComment;
      
      if (indirectNetDebt && indirectNetDebt > 0) {
        return `${formatCurrency(indirectNetDebt)}\n\n${comment}`;
      } else {
        return comment;
      }
    },
  },

  // Energy declaration
  {
    key: 'mainResidentialBuilding.energyDeclaration.completed',
    label: 'Energideklaration',
    condition: (context) => {
      const value = context.filteredProviderData?.mainResidentialBuilding?.energyDeclaration?.completed;
      return value === "Ordered";
    },
    formatter: () => 'Beställd',
  },



  {
    key: 'energyDeclaration.primaryEnergyNumber',
    label: 'Energiprestanda',
    formatter: (value) => `${value} kWh/kvm och år`,
  },

  {
    key: 'mainResidentialBuilding.energyDeclaration.primaryEnergyNumber',
    label: 'Energiprestanda',
    formatter: (value) => `${value} kWh/kvm och år`,
  },


  {
    key: 'energyDeclaration.energyClass',
    label: 'Energiklass',
  },

  {
    key: 'mainResidentialBuilding.energyDeclaration.energyClass',
    label: 'Energiklass',
  },

  // Inspection fields - inserted in logical order
  {
    key: 'inspection.inspectedAndInsured',
    label: 'Besiktad och försäkrad',
    condition: (context) => {
      const { inspection } = context.filteredProviderData || {};
      const { inspected, sellerInsurence } = inspection || {};
      return inspected && sellerInsurence;
    },
    formatter: () => "Bostaden är besiktad och säljaren avser att teckna en försäkring mot dolda fel."
  },

  {
    key: 'inspection.inspected',
    label: 'Besiktad',
    condition: (context) => {
      const { inspection } = context.filteredProviderData || {};
      const { inspected, sellerInsurence } = inspection || {};
      return inspected && !sellerInsurence;
    },
    formatter: () => "Bostaden är förbesiktad av säljaren"
  },

  {
    key: 'inspection.insured',
    label: 'Försäkrad',
    condition: (context) => {
      const { inspection } = context.filteredProviderData || {};
      const { inspected, sellerInsurence } = inspection || {};
      return !inspected && sellerInsurence;
    },
    formatter: () => "Bostaden är försäkrad mot dolda fel."
  },

  {
    key: 'waterAndDrain.info',
    label: 'Vatten och avlopp',
    estateTypes: forEstateTypes('Plot'),
  },

  // House/Cottage specific fields
  {
    key: 'enrollments.preferentialAndCommunity',
    label: 'Servitut och samfälligheter',
    // estateTypes: forEstateTypes('House', 'Cottage'),
    source: 'provider',
  },

  {
    key: 'plan.type',
    label: 'Planbestämmelser',
    estateTypes: forEstateTypes('House', 'Cottage', 'Farm', 'Plot', 'HousingCooperative', 'Condominium'),
    source: 'provider',
  },

  {
    key: 'enrollments.planRegulations',
    label: 'Planbestämmelser',
    estateTypes: forEstateTypes('CommercialProperty'),
    source: 'provider'
  },

  {
    key: 'baseInformation.tenure',
    label: 'Upplåtelseform',
    formatter: (value) => getTenureValue(value),
  },
  {
    key: 'baseInformation.disposalForm',
    label: 'Upplåtelseform',
    estateTypes: forEstateTypes('CommercialProperty'),
    formatter: (value) => getTenureValue(value),
  },
  {
    key: 'plotBaseInformation.tenure',
    label: 'Upplåtelseform',
    formatter: (value) => getTenureValue(value),
  },

  {
    key: 'description.wayDescription',
    label: 'Vägbeskrivning',
  },

  // {
  //   key: 'description.other',
  //   label: 'Övrigt',
  // },


  {
    key: 'assess.taxAssessmentYear',
    label: 'Taxeringsår',
    // estateTypes: forEstateTypes('House', 'Cottage'),
    source: 'provider',
  },  

  {
    key: 'assess.totalAssessedValue',
    label: 'Taxeringsvärde',
    estateTypes: forEstateTypes('House', 'Cottage', 'Condominium', 'CommercialProperty'),
    source: 'provider',
    condition: (context) => {
      const value = context.provider_data?.assess?.totalAssessedValue;
      return value > 0;
    },
    formatter: (value) => formatCurrency(value),
  },

  {
    key: 'assess.totalAssessedValue',
    label: 'Taxeringsvärde',
    estateTypes: forEstateTypes('Farm'),
    source: 'provider',
    condition: (context) => {
      const value = context.provider_data?.assess?.totalAssessedValue;
      return value > 0;
    },
    formatter: (value) => value === 0 ? `${value} kr` : formatCurrency(value),
  },

  {
    key: 'assess.buildingValue',
    label: 'Byggnadsvärde',
    estateTypes: forEstateTypes('House', 'Cottage', 'Condominium', 'CommercialProperty'),
    source: 'provider',
    condition: (context) => {
      const value = context.provider_data?.assess?.buildingValue;
      return value > 0;
    },
    formatter: (value) => value === 0 ? `${value} kr` : formatCurrency(value),
  },


  {
    key: 'assess.landValue',
    label: 'Markvärde',
    estateTypes: forEstateTypes('House', 'Cottage', 'Condominium', 'CommercialProperty'),
    source: 'provider',
    condition: (context) => {
      const value = context.provider_data?.assess?.landValue;
      return value > 0;
    },
    formatter: (value) => value === 0 ? `${value} kr` : formatCurrency(value),
  },

    // Farm-specific fields
    {
      key: 'assessments',
      label: 'Byggnadsvärde',
      estateTypes: forEstateTypes('Farm'),
      condition: (context) => {
        const assessments = context.provider_data?.assessments;
        const assessValues = assessments[0]?.assessValues;
        if (!assessValues || assessValues.length === 0) return false;
  
        const buildingValue = assessValues.filter((item: { evaluationUnit: string; value: number; valueYear?: string }) => item.evaluationUnit.toLowerCase().includes('byggnad')).map((item: { evaluationUnit: string; value: number; valueYear?: string }) => item.value).reduce((a: number, b: number) => a + b, 0);
        return buildingValue > 0;
      },
      source: 'provider',
      formatter: (value) => {
        if (!value || value.length === 0) return '';
        
        const assessValues = value[0]?.assessValues;
        if (!assessValues || assessValues.length === 0) return '';
  
        const buildingValue = assessValues.filter((item: { evaluationUnit: string; value: number; valueYear?: string }) => item.evaluationUnit.toLowerCase().includes('byggnad')).map((item: { evaluationUnit: string; value: number; valueYear?: string }) => item.value).reduce((a: number, b: number) => a + b, 0);
        
        return formatCurrency(buildingValue);
      },
    },
  
    {
      key: 'assessments',
      label: 'Markvärde',
      estateTypes: forEstateTypes('Farm'),
      condition: (context) => {
        const assessments = context.provider_data?.assessments;
        const assessValues = assessments[0]?.assessValues;
        if (!assessValues || assessValues.length === 0) return false;
  
        const landValue = assessValues.filter((item: { evaluationUnit: string; value: number; valueYear?: string }) => item.evaluationUnit.toLowerCase().includes('mark')).map((item: { evaluationUnit: string; value: number; valueYear?: string }) => item.value).reduce((a: number, b: number) => a + b, 0);
        return landValue > 0;
      },
      source: 'provider',
      formatter: (value) => {
        if (!value || value.length === 0) return '';
        
        const assessValues = value[0]?.assessValues;
        if (!assessValues || assessValues.length === 0) return '';
  
        const landValue = assessValues.filter((item: { evaluationUnit: string; value: number; valueYear?: string }) => item.evaluationUnit.toLowerCase().includes('mark')).map((item: { evaluationUnit: string; value: number; valueYear?: string }) => item.value).reduce((a: number, b: number) => a + b, 0);
        
        
        return formatCurrency(landValue);
      },
    },

    {
      key: 'assessments',
      label: 'Skogsvärde',
      estateTypes: forEstateTypes('Farm'),
      condition: (context) => {
        const assessments = context.provider_data?.assessments;
        const assessValues = assessments[0]?.assessValues;
        if (!assessValues || assessValues.length === 0) return false;
  
        const landValue = assessValues.filter((item: { evaluationUnit: string; value: number; valueYear?: string }) => item.evaluationUnit.toLowerCase().includes('skog')).map((item: { evaluationUnit: string; value: number; valueYear?: string }) => item.value).reduce((a: number, b: number) => a + b, 0);
        return landValue > 0;
      },
      source: 'provider',
      formatter: (value) => {
        if (!value || value.length === 0) return '';
        
        const assessValues = value[0]?.assessValues;
        if (!assessValues || assessValues.length === 0) return '';
  
        const landValue = assessValues.filter((item: { evaluationUnit: string; value: number; valueYear?: string }) => item.evaluationUnit.toLowerCase().includes('skog')).map((item: { evaluationUnit: string; value: number; valueYear?: string }) => item.value).reduce((a: number, b: number) => a + b, 0);
        
        
        return formatCurrency(landValue);
      },
    },
  

  {
    key: 'assess.taxFee',
    label: 'Fastighetsavgift',
    // estateTypes: forEstateTypes('House', 'Cottage'),
    condition: (context) => {
      const value = context.provider_data?.assess?.taxFee;
      return value > 0;
    },
    source: 'provider',
    formatter: (value) => `${formatCurrency(value)} per år`,
  },

  {
    key: 'assess.leasholdFee',
    label: 'Tomträttsavgäld',
    condition: (context) => {
      const value = context.provider_data?.assess?.leasholdFee;
      return value > 0;
    },
    source: 'provider',
    formatter: (value) => `${formatCurrency(value)} per år`,
  },

  {
    key: 'assess.leasholdUntil',
    label: 'Tomträtt till',
    condition: (context) => {
      const value = context.provider_data?.assess?.leasholdUntil;
      return !!value;
    },
    source: 'provider',
    formatter: (value) => {
      if (!value) return '';
      try {
        const date = new Date(value);
        return date.toLocaleDateString('sv-SE', { 
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        });
      } catch {
        return value;
      }
    },
  },


  // New assessments array structure (VITEC updated format)
  // {
  //   key: 'assessments',
  //   label: 'Fastighetsavgift',
  //   estateTypes: forEstateTypes('House', 'Cottage', 'Farm', 'Condominium', 'CommercialProperty'),
  //   condition: (context) => {
  //     const taxFeeValue = context.provider_data?.assess?.taxFee;
  //     const assessments = context.provider_data?.assessments;
  //     return assessments && assessments.length > 0 && assessments[0]?.taxFee > 0;
  //   },
  //   source: 'provider',
  //   formatter: (value) => {
  //     const taxFee = value?.[0]?.taxFee;
  //     return taxFee ? `${formatCurrency(taxFee)} per år` : '';
  //   },
  // },


  {
    key: 'propertyTypeAndAreas.jointlyTaxedProperties',
    label: 'Samtaxerade fastigheter',
    // estateTypes: forEstateTypes('House', 'Cottage'),
    source: 'provider'
  },


  {
    key: 'mortages',
    label: 'Pantbrev',
    // estateTypes: forEstateTypes('House', 'Cottage'),
    source: 'provider',
    condition: (context) => {
      const value = context.provider_data?.mortages;
      return value && value.length > 0;
    },
    formatter: (value) => 
      `${value.length || 0} st pantbrev på totalt ${formatCurrency(getMortgagesAmount(value))}`,
  },


];
